<script setup>
  import { useUserStore } from '@/stores/user'
  import { useRouter, useRoute} from 'vue-router'
  const userStore = useUserStore()
  import { useMainStore } from '@/stores/main'
  let mainStore = useMainStore()

  import PasswordResetSentModal from '@/components/modals/PasswordResetSent.vue'

  import { onMounted,ref } from 'vue'
  import Slideover from '../../../components/SlideOver/slideover.vue'
  import CreateForm from '../../../components/SlideOver/forms/CreateApiKey.vue'
  import User from '../../../api/User';
  import moment from 'moment'
  import Card from '../../../components/Card.vue'

  import Usage from '@/components/Account/Usage.vue'

  const reset_running = ref(false)

  async function reset_password() {
     // if we are processing a reset request, don't take another
    if (reset_running.value == true) return
    reset_running.value = true
    let res = await User.request_password_reset(userStore.email)

    // show modal
    reset_running.value = false

    mainStore.show_modal(PasswordResetSentModal, { })
  }

  function getTypeLabel(type) {
    const typeMap = {
      1: 'Free',
      2: 'Basic',
      3: 'Professional',
      4: 'Elite',
      5: 'Enterprise',
      1337: 'Admin'
    }
    return typeMap[type] || 'Unknown'
  }

  function getTypeClass(type) {
    const classMap = {
      1: 'free',
      2: 'basic',
      3: 'professional',
      4: 'elite',
      5: 'enterprise',
      1337: 'admin'
    }
    return classMap[type] || 'unknown'
  }
</script>

<template>
  <div class="information-content">
    <!-- Usage Stats -->
    <div class="usage-section">
      <Usage/>
    </div>

    <!-- Account Information -->
    <div class="section">
      <h5 class="section-title">Account Information</h5>

      <div class="info-grid">
        <div class="info-card">
          <label class="info-label">Name</label>
          <div class="info-value">{{ userStore.name }}</div>
        </div>

        <div class="info-card">
          <label class="info-label">Email</label>
          <div class="info-value">{{ userStore.email }}</div>
        </div>

        <div class="info-card">
          <label class="info-label">Account Type</label>
          <div class="info-value">
            <span :class="'type-badge type-' + getTypeClass(userStore.accountType)">
              {{ getTypeLabel(userStore.accountType) }}
            </span>
            <span v-if="userStore.organizationId" class="inherited-note">
              <font-awesome-icon :icon="['fas', 'building']" /> from organization
            </span>
          </div>
        </div>

        <div class="info-card">
          <label class="info-label">Organization</label>
          <div class="info-value" v-if="userStore.organizationId">{{ userStore.organizationName || 'Unknown' }}</div>
          <div class="info-value no-org" v-else>—</div>
        </div>

        <div class="info-card password-card">
          <label class="info-label">Password</label>
          <div class="info-value">*****************</div>

          <button class="reset-btn" @click="reset_password()" :disabled="reset_running">
            <font-awesome-icon v-if="reset_running" :icon="['fas', 'spinner']" spin />
            <font-awesome-icon v-else :icon="['fas', 'arrow-rotate-left']" />
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.information-content {
  padding: 0;
}

.usage-section {
  margin-bottom: 2rem;
}

.section {
  margin-bottom: 2rem;
}

.section-title {
  color: #9CA3AF;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.info-card {
  background: rgba(26, 29, 36, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  padding: 1rem;
  position: relative;
  transition: all 0.2s ease;
}

.info-card:hover {
  background: rgba(26, 29, 36, 0.6);
  border-color: rgba(255, 255, 255, 0.12);
}

.info-label {
  display: block;
  color: #9CA3AF;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.info-value {
  color: #E5E7EB;
  font-size: 1rem;
  font-weight: 500;
}

.password-card {
  position: relative;
}

.reset-btn {
  position: absolute;
  right: 1rem;
  top: 1rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.reset-btn:hover:not(:disabled) {
  color: #a855f7;
  border-color: rgba(168, 85, 247, 0.3);
  background: rgba(168, 85, 247, 0.1);
}

.reset-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.type-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
  display: inline-block;
}

.type-free {
  background: rgba(107, 114, 128, 0.15);
  color: #9CA3AF;
}

.type-basic {
  background: rgba(59, 130, 246, 0.15);
  color: #3B82F6;
}

.type-professional {
  background: rgba(168, 85, 247, 0.15);
  color: #A855F7;
}

.type-elite {
  background: rgba(245, 158, 11, 0.15);
  color: #F59E0B;
}

.type-enterprise {
  background: rgba(16, 185, 129, 0.15);
  color: #10B981;
}

.type-admin {
  background: rgba(239, 68, 68, 0.15);
  color: #EF4444;
}

.inherited-note {
  display: inline-block;
  margin-left: 0.5rem;
  font-size: 0.75rem;
  color: #7357D2;
  opacity: 0.8;
}

.no-org {
  color: #6B7280;
  font-style: italic;
}
</style>
